import "client-only";

import type { Faro } from "@grafana/faro-web-sdk";

import {
  CLIENT_TRACE_COLLECTOR_PATH,
  CLIENT_TRACE_SESSION_STORAGE_KEY,
  resolveClientTraceActivation,
} from "./client-trace-config";
import {
  createClientTraceAttributes,
  serializeClientTraceConsoleArgs,
  type T_ClientTraceAttributes,
} from "./client-trace-event";
import { sanitizeClientTracePathname } from "./client-trace-page";
import { C_ClientTraceRateLimiter } from "./client-trace-rate-limiter";

export type { T_ClientTraceAttributes } from "./client-trace-event";

const MAX_STRUCTURED_TRACE_EVENTS_PER_SECOND = 30;
const MAX_TRANSPORT_ITEMS_PER_SECOND = 60;

type T_ClientTraceEvent = {
  attributes?: T_ClientTraceAttributes;
  clientElapsedMs: number;
  event: string;
  scope: string;
  sequence: number;
  timestampMs: number;
};

type T_ClientTraceLifecycle =
  | { status: "disabled"; faro?: Faro }
  | {
      status: "initializing";
      promise: Promise<boolean>;
      sessionId: string;
    }
  | { status: "ready"; faro: Faro; sessionId: string }
  | { status: "failed" };

type T_InitializeClientTraceArgs = {
  appEnvironment: string;
  search?: string;
};

type T_LogClientTraceArgs = {
  attributes?: T_ClientTraceAttributes;
  event: string;
  scope: string;
};

let activationVersion = 0;
let lifecycle: T_ClientTraceLifecycle = { status: "disabled" };
let nextSequence = 0;
const structuredTraceRateLimiter = new C_ClientTraceRateLimiter(
  MAX_STRUCTURED_TRACE_EVENTS_PER_SECOND,
);

function readStoredSession(): string | null {
  try {
    return sessionStorage.getItem(CLIENT_TRACE_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function applyStorageAction(
  action: "clear" | "none" | "persist",
  sessionId?: string,
): void {
  try {
    if (action === "clear") {
      sessionStorage.removeItem(CLIENT_TRACE_SESSION_STORAGE_KEY);
    } else if (action === "persist" && sessionId) {
      sessionStorage.setItem(CLIENT_TRACE_SESSION_STORAGE_KEY, sessionId);
    }
  } catch {
    // Privacy modes may disable sessionStorage. The active page can still trace.
  }
}

function createTransportRateGuard() {
  const limiter = new C_ClientTraceRateLimiter(MAX_TRANSPORT_ITEMS_PER_SECOND);
  limiter.reset(Date.now());

  return <T_Item>(item: T_Item): T_Item | null =>
    limiter.consume(Date.now()).allowed ? item : null;
}

function getSanitizedPage(): { id: string; url: string } {
  const pathname = sanitizeClientTracePathname(window.location.pathname);
  return {
    id: pathname,
    url: `${window.location.origin}${pathname}`,
  };
}

function setFaroSession(faro: Faro, sessionId: string): void {
  const currentSession = faro.api.getSession();
  faro.api.setSession({
    ...currentSession,
    attributes: {
      ...currentSession?.attributes,
      trace_session: sessionId,
    },
  });
  faro.api.setPage(getSanitizedPage());
}

function emitTraceEvent(
  faro: Faro,
  sessionId: string,
  traceEvent: T_ClientTraceEvent,
): void {
  faro.api.pushEvent(
    traceEvent.event,
    createClientTraceAttributes({
      attributes: traceEvent.attributes,
      clientElapsedMs: traceEvent.clientElapsedMs,
      sequence: traceEvent.sequence,
      traceSession: sessionId,
    }),
    traceEvent.scope,
    {
      skipDedupe: true,
      timestampOverwriteMs: traceEvent.timestampMs,
    },
  );
}

function startTraceSession(faro: Faro, sessionId: string): void {
  setFaroSession(faro, sessionId);
  faro.unpause();
  nextSequence = 0;
  structuredTraceRateLimiter.reset(performance.now());
  lifecycle = { status: "ready", faro, sessionId };
  logClientTrace({
    event: "trace.started",
    scope: "client-observability",
    attributes: {
      page: sanitizeClientTracePathname(window.location.pathname),
    },
  });
}

async function startFaro(
  sessionId: string,
  expectedActivationVersion: number,
): Promise<boolean> {
  try {
    const {
      ConsoleInstrumentation,
      initializeFaro,
      LogLevel,
      SessionInstrumentation,
      WebVitalsInstrumentation,
    } = await import("@grafana/faro-web-sdk");
    const page = getSanitizedPage();
    const faro = initializeFaro({
      app: {
        environment: "local",
        name: "lattice-frontend",
      },
      batching: {
        enabled: true,
        itemLimit: 50,
        sendTimeout: 1_000,
      },
      beforeSend: createTransportRateGuard(),
      consoleInstrumentation: {
        disabledLevels: [LogLevel.TRACE, LogLevel.DEBUG],
      },
      instrumentations: [
        new WebVitalsInstrumentation(),
        new ConsoleInstrumentation(),
        new SessionInstrumentation(),
      ],
      logArgsSerializer: serializeClientTraceConsoleArgs,
      pageTracking: { page },
      sessionTracking: {
        persistent: false,
        session: {
          attributes: { trace_session: sessionId },
        },
      },
      url: CLIENT_TRACE_COLLECTOR_PATH,
    });

    if (expectedActivationVersion !== activationVersion) {
      faro.pause();
      lifecycle = { status: "disabled", faro };
      return false;
    }

    startTraceSession(faro, sessionId);
    return true;
  } catch {
    if (expectedActivationVersion === activationVersion) {
      lifecycle = { status: "failed" };
    }
    console.warn("[Client trace initialization failed]");
    return false;
  }
}

export function initializeClientTrace({
  appEnvironment,
  search = window.location.search,
}: T_InitializeClientTraceArgs): Promise<boolean> {
  const activation = resolveClientTraceActivation({
    appEnvironment,
    search,
    storedSession: readStoredSession(),
  });
  if (activation.status === "disabled") {
    applyStorageAction(activation.storageAction);
    activationVersion += 1;
    const existingFaro =
      lifecycle.status === "ready" || lifecycle.status === "disabled"
        ? lifecycle.faro
        : undefined;
    existingFaro?.pause();
    lifecycle = existingFaro
      ? { status: "disabled", faro: existingFaro }
      : { status: "disabled" };
    return Promise.resolve(false);
  }

  applyStorageAction(activation.storageAction, activation.sessionId);
  if (lifecycle.status === "ready") {
    if (lifecycle.sessionId !== activation.sessionId) {
      startTraceSession(lifecycle.faro, activation.sessionId);
    } else {
      setFaroSession(lifecycle.faro, lifecycle.sessionId);
    }
    return Promise.resolve(true);
  }
  if (lifecycle.status === "disabled" && lifecycle.faro) {
    startTraceSession(lifecycle.faro, activation.sessionId);
    return Promise.resolve(true);
  }
  if (lifecycle.status === "initializing") {
    if (lifecycle.sessionId === activation.sessionId) return lifecycle.promise;
    return lifecycle.promise.then(() =>
      initializeClientTrace({ appEnvironment, search }),
    );
  }
  if (lifecycle.status === "failed") return Promise.resolve(false);

  activationVersion += 1;
  const expectedActivationVersion = activationVersion;
  const promise = startFaro(activation.sessionId, expectedActivationVersion);
  lifecycle = {
    status: "initializing",
    promise,
    sessionId: activation.sessionId,
  };
  return promise;
}

export function isClientTraceActive(): boolean {
  return lifecycle.status === "ready";
}

export function logClientTrace({
  attributes,
  event,
  scope,
}: T_LogClientTraceArgs): void {
  if (lifecycle.status !== "ready") return;

  const clientElapsedMs = performance.now();
  const rateDecision = structuredTraceRateLimiter.consume(clientElapsedMs);
  if (!rateDecision.allowed) return;

  const traceEvent: T_ClientTraceEvent = {
    attributes:
      rateDecision.droppedSinceLast > 0
        ? {
            ...attributes,
            dropped_since_last: rateDecision.droppedSinceLast,
          }
        : attributes,
    clientElapsedMs,
    event,
    scope,
    sequence: nextSequence,
    timestampMs: Date.now(),
  };
  nextSequence += 1;
  emitTraceEvent(lifecycle.faro, lifecycle.sessionId, traceEvent);
}
